import React, { useState } from 'react';
import { Modal, Input, List, Spin, Button, Select } from 'antd';
import { useGetPersonnel } from '../../hooks/genericHooks';
import { getAnchoredModalStyle } from './modalPositioning';

// Accept unified dynamic modal props
export type PersonnelSelectMode = 'default' | 'code_only' | 'name_only' | 'both' | 'extended';

export interface PersonnelSelectProps {
  open: boolean;
  value?: any;
  onOk: (value: any) => void;
  onCancel: () => void;
  afterClose?: () => void;
  anchorRect?: DOMRect | null;
  options?: any[];
  mode?: PersonnelSelectMode; // controls returned value
  multi?: boolean; // when true, allow selecting multiple personnel
  // When true, onOk returns an object including all mode outputs and raw selection for display/testing
  returnAllModes?: boolean;
}

const PersonnelSelect: React.FC<PersonnelSelectProps> = ({ open, value, onOk, onCancel, afterClose, anchorRect, options, mode = 'default', multi = false, returnAllModes = false }) => {
  const { data: personnelData = [], isLoading } = useGetPersonnel();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(multi ? null : (value || null));
  const [selectedIds, setSelectedIds] = useState<string[]>(Array.isArray(value) ? (value as any[]).map(v => String(v)) : []);
  const [companyFilter, setCompanyFilter] = useState('');
  const [occupationFilter, setOccupationFilter] = useState('');

  const modalWidth = 520;

  // Normalize data structure
  const personnelList = Array.isArray(personnelData)
    ? personnelData
    : (personnelData?.data || []);

  // Get unique company and occupation lists
  const companies = Array.from(new Set(personnelList.map((p: any) => typeof p.company === 'object' && p.company !== null ? p.company.name : p.company).filter(Boolean)));
  const occupations = Array.from(new Set(personnelList.map((p: any) => p.occupation).filter(Boolean)));

  // Combined filter
  const filteredPersonnel = personnelList.filter((p: any) => {
    const personCompany = typeof p.company === 'object' && p.company !== null ? p.company.name : p.company;
    const matchesCompany = companyFilter ? personCompany === companyFilter : true;
    const matchesOccupation = occupationFilter ? p.occupation === occupationFilter : true;
    return matchesCompany && matchesOccupation;
  });

  // Shared confirm handler to avoid duplication
  const handleConfirm = () => {
    if (multi) {
      const selected = (personnelList || []).filter((p: any) => selectedIds.includes(String(p.pers_id)));
      if (returnAllModes) {
        const codes = selected.map((p: any) => String(p.pers_id));
        const combined = selected.map((p: any) => `${String(p.pers_id)} - ${`${p.first_name} ${p.last_name}`.trim()}`);
        onOk({
          type: 'personnel',
          multi: true,
          selected,
          modes: {
            code_only: codes,
            default: combined,
          },
        });
      } else if (mode === 'code_only') {
        const codes = selected.map((p: any) => String(p.pers_id));
        onOk(codes);
      } else {
        const combined = selected.map((p: any) => `${String(p.pers_id)} - ${`${p.first_name} ${p.last_name}`.trim()}`);
        onOk(combined);
      }
    } else {
      const selectedPersonnel = (personnelList || []).find((p: any) => p.pers_id === selectedId);
      if (selectedPersonnel) {
        const fullName = `${selectedPersonnel.first_name} ${selectedPersonnel.last_name}`.trim();
        const codeStr = String(selectedPersonnel.pers_id);
        const combined = `${codeStr} - ${fullName}`;
        const numericId = selectedPersonnel.id ?? null;
        const email = selectedPersonnel.email ?? null;
        const phone = selectedPersonnel.phone ?? null;
        if (returnAllModes) {
          onOk({
            type: 'personnel',
            multi: false,
            selected: selectedPersonnel,
            modes: {
              code_only: codeStr,
              name_only: fullName,
              both: [combined, codeStr, fullName, numericId],
              extended: [combined, codeStr, fullName, numericId, email, phone],
              default: combined,
            },
          });
        } else {
          const payload =
            mode === 'code_only' ? codeStr :
            mode === 'name_only' ? fullName :
            mode === 'both' ? [combined, codeStr, fullName, numericId] :
            mode === 'extended' ? [combined, codeStr, fullName, numericId, email, phone] :
            combined;
          onOk(payload);
        }
      }
    }
  };

  return (
    <Modal
      open={open}
      title="Select Personnel"
      onCancel={onCancel}
      destroyOnClose
      maskClosable
      footer={null}
      centered={!anchorRect}
      style={anchorRect ? getAnchoredModalStyle(anchorRect, { width: modalWidth, approxHeight: 420 }) : undefined}
      afterClose={afterClose}
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <Select
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              if (!option) return false;
              const label = String(option.children ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            style={{ minWidth: 120 }}
            placeholder="Filter by company"
            value={companyFilter || undefined}
            onChange={v => setCompanyFilter(v || '')}
          >
            {companies.map(c => (
              <Select.Option key={c} value={c}>{c}</Select.Option>
            ))}
          </Select>
          <Select
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              if (!option) return false;
              const label = String(option.children ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            style={{ minWidth: 120 }}
            placeholder="Filter by occupation"
            value={occupationFilter || undefined}
            onChange={v => setOccupationFilter(v || '')}
          >
            {occupations.map(o => (
              <Select.Option key={o} value={o}>{o}</Select.Option>
            ))}
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" onClick={handleConfirm} disabled={multi ? (selectedIds.length === 0) : !selectedId}>Select</Button>
        </div>
      </div>
      <Select
        showSearch
        style={{ width: '100%', borderRadius: 0, background: '#e6f2ff', border: 'none' }}
        placeholder="Select personnel"
        mode={multi ? 'multiple' : undefined}
        value={multi ? selectedIds.map(x => String(x)) : (selectedId ?? undefined)}
        onChange={(v) => {
          if (multi) setSelectedIds((v as any[]).map(x => String(x)));
          else setSelectedId(v as number);
        }}
        optionFilterProp="children"
        dropdownStyle={{ borderRadius: 0 }}
        filterOption={(input, option) => {
          if (!option) return false;
          const haystack = String(option.children ?? '').toLowerCase();
          const tokens = input.toLowerCase().split(/\s+/).filter(Boolean);
          return tokens.every(t => haystack.includes(t));
        }}
      >
        {filteredPersonnel.map((person: any) => (
          <Select.Option key={String(person.pers_id)} value={String(person.pers_id)}>
            {`${person.pers_id} - ${person.first_name} ${person.last_name}`}
            {(() => {
              if (typeof person.company === 'object' && person.company !== null) {
                return ` (${person.company.name})`;
              } else if (person.company) {
                return ` (${person.company})`;
              }
              return '';
            })()}
            {person.occupation ? ` - ${person.occupation}` : ''}
          </Select.Option>
        ))}
      </Select>
    </Modal>
  );
};

export default PersonnelSelect;
